import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import type { RowPinningState } from '@tanstack/react-table';
import type {
  MRT_ColumnOrderState,
  MRT_SortingState,
  MRT_Updater,
  MRT_VisibilityState,
} from 'material-react-table';
import type { ReactNode } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { MdArrowDownward, MdArrowUpward, MdRestartAlt, MdSave, MdTune } from 'react-icons/md';
import {
  getOrderedColumnOptions,
  type BaseTableColumnOption,
  type BaseTablePreferences,
  type BaseTableState,
} from './baseTablePreferences';

const TABLE_OPTIONS_CONFIG = {
  strategy: 'fixed' as const,
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
  ],
};

type BaseTableOptionsProps = {
  className?: string;
  columnOptions: BaseTableColumnOption[];
  initialState: BaseTableState;
  saveTablePreferences: (storageKey: string, preferences: BaseTablePreferences) => void;
  setColumnOrder: (updater: MRT_Updater<MRT_ColumnOrderState>) => void;
  setRowPinning: (updater: MRT_Updater<RowPinningState>) => void;
  setColumnVisibility: (updater: MRT_Updater<MRT_VisibilityState>) => void;
  setSorting: (updater: MRT_Updater<MRT_SortingState>) => void;
  storageKey: string;
  startContent?: ReactNode;
  tablePreferences: BaseTablePreferences;
};

const BaseTableOptions = ({
  className = '',
  columnOptions,
  initialState,
  saveTablePreferences,
  setColumnOrder,
  setRowPinning,
  setColumnVisibility,
  setSorting,
  storageKey,
  startContent,
  tablePreferences,
}: BaseTableOptionsProps): JSX.Element => {
  const orderedColumnOptions = getOrderedColumnOptions(columnOptions, tablePreferences.columnOrder);
  const primarySort = tablePreferences.sorting[0];

  const setColumnVisible = (columnId: string, visible: boolean): void => {
    setColumnVisibility({
      ...tablePreferences.columnVisibility,
      [columnId]: visible,
    });
  };

  const moveColumn = (columnId: string, direction: number): void => {
    const order = [...tablePreferences.columnOrder];
    const index = order.indexOf(columnId);
    const targetIndex = index + direction;

    if (index < 0 || targetIndex < 0 || targetIndex >= order.length) return;

    [order[index], order[targetIndex]] = [order[targetIndex], order[index]];
    setColumnOrder(order);
  };

  const setPrimarySort = (columnId: string): void => {
    setSorting(columnId ? [{ id: columnId, desc: false }] : []);
  };

  const setPrimarySortDirection = (desc: boolean): void => {
    if (!primarySort) return;
    setSorting([{ ...primarySort, desc }]);
  };

  const resetTablePreferences = (): void => {
    setColumnOrder(initialState.columnOrder || columnOptions.map((column) => column.id));
    setColumnVisibility(initialState.columnVisibility || {});
    setRowPinning(initialState.rowPinning || { top: [], bottom: [] });
    setSorting(initialState.sorting || []);
  };

  const primarySortOptions = columnOptions.map((column) => ({
    value: column.id,
    label: column.label,
  }));

  return (
    <div
      className={`d-flex align-items-center ${
        startContent ? 'justify-content-between' : 'justify-content-end'
      } gap-2 mb-2 ${className}`.trim()}
    >
      {startContent}
      <Dropdown autoClose="outside" align="end">
        <Dropdown.Toggle variant="secondary" size="sm" className="d-flex align-items-center gap-2">
          <MdTune />
          Table Options
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="base-table-options-menu bg-primary-shadow border border-secondary p-3 customScrollbar-Y"
          flip={false}
          popperConfig={TABLE_OPTIONS_CONFIG}
          renderOnMount
          style={{
            minWidth: 340,
            maxHeight: 'calc(100dvh - 1rem)',
            overflowY: 'auto',
            zIndex: 'var(--z-popover)',
          }}
        >
          <div className="fw-bold mb-2">Fields</div>
          <div className="d-flex flex-column gap-2 mb-3">
            {orderedColumnOptions.map((column, index) => (
              <div
                key={column.id}
                className="d-flex align-items-center justify-content-between gap-2"
              >
                <Form.Check
                  id={`base-table-column-${storageKey}-${column.id}`}
                  type="checkbox"
                  className="m-0"
                  label={column.label}
                  checked={tablePreferences.columnVisibility[column.id] !== false}
                  disabled={column.required}
                  onChange={(event) => setColumnVisible(column.id, event.target.checked)}
                />
                <div className="d-flex align-items-center gap-1">
                  {column.required && <span className="small text-secondary">Required</span>}
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="d-flex align-items-center justify-content-center p-1"
                    aria-label={`Move ${column.label} up`}
                    disabled={index === 0}
                    onClick={() => moveColumn(column.id, -1)}
                  >
                    <MdArrowUpward />
                  </Button>
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="d-flex align-items-center justify-content-center p-1"
                    aria-label={`Move ${column.label} down`}
                    disabled={index === orderedColumnOptions.length - 1}
                    onClick={() => moveColumn(column.id, 1)}
                  >
                    <MdArrowDownward />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="fw-bold mb-2">Sort</div>
          {/*
            Clearing the select is the only way back to "No primary sort", and the menu has to
            stay inline: the enclosing Dropdown closes on any click outside its menu element, so
            clicking a portalled option would collapse the whole Table Options panel.
          */}
          <BaseSelect
            wrapperClassName="mb-2"
            isClearable
            isSearchable
            portalMenu={false}
            selectProps={{
              'aria-label': 'Primary sort',
              options: primarySortOptions,
              value: findSelectOption(primarySortOptions, primarySort?.id || ''),
              placeholder: 'No primary sort',
              onChange: (option: { value: string } | null) => setPrimarySort(option?.value ?? ''),
            }}
          />

          <div className="d-flex gap-2 mb-3">
            <Button
              variant={primarySort && !primarySort.desc ? 'light' : 'outline-light'}
              size="sm"
              aria-label="Ascending"
              disabled={!primarySort}
              onClick={() => setPrimarySortDirection(false)}
            >
              Asc
            </Button>
            <Button
              variant={primarySort?.desc ? 'light' : 'outline-light'}
              size="sm"
              aria-label="Descending"
              disabled={!primarySort}
              onClick={() => setPrimarySortDirection(true)}
            >
              Desc
            </Button>
          </div>

          <div className="d-flex justify-content-between gap-2">
            <Button
              variant="outline-light"
              size="sm"
              className="d-flex align-items-center gap-2"
              onClick={resetTablePreferences}
            >
              <MdRestartAlt />
              Reset table
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center gap-2"
              onClick={() => saveTablePreferences(storageKey, tablePreferences)}
            >
              <MdSave />
              Save
            </Button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default BaseTableOptions;
